import { GameState } from './GameState';
import { GameConfig } from '../core/GameConfig';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameData } from '../data/GameData';
import { Utils } from '../core/Utils';
import { fanSystem } from './FanSystem';
import { rankSystem } from './RankSystem';
import { normalNpcSystem } from './NormalNpcSystem';
import { assistantSystem } from './AssistantSystem';
import {
    absMonthOf, AWARD_MAP, AwardDef, AwardId,
    AWARD_CATEGORY_ORDER, CEREMONIES, CeremonyDef, windowOf,
} from '../data/tables/Awards';
import { AwardCategory, AwardNominee, AwardRecord, AwardTier, ScriptGenre, ScriptDef, Contract } from '../data/types';
import { fixedCastCredits } from '../data/Cast';
import { dramaPremiereAbsOf, scriptAirMonths, scriptShootMonths, dramaTimelineOf, scriptPremiereAbsOf, scriptSystem } from './ScriptSystem';

/**
 * 年度奖项系统（金枢奖 / 星汉奖，单例）
 * 设计文档：docs/新增功能-年度奖项与网友评价.md
 *
 * 定稿规则：
 *  - 候选 = 履历行 + **在途已杀青作品**（玩家 airing 合同 / NPC airing 工作），时间戳一律取**杀青月**
 *      （2026-09-30 用户改定：杀青进播放期即参评，⛔ 等播完；本系统仍⛔ 新增作品记录结构）；
 *  - 参评档位由 roleType 实时派生（主角型一律主角档，⛔ 不落存档），历届结果固化在 awardHistory；
 *  - 同一 (scriptId, roleIndex) 全周目只计首次（市场兜底③允许重拍，不去重会出现同片两届得主不同）；
 *  - 评选随机只在提名时 roll 一次并冻结进名单，颁奖夜只取首位——⛔ 刷新不许名单重排；
 *  - 提名规则（2026-09-30 茄子拍板）：参评线 score ≥ AWARD_NOMINEE_MIN_SCORE + 同类别同人限
 *    AWARD_PERFORMER_NOMINEE_CAP 部（代表作入围，先过滤再选代表作）+ 名额 4~5 浮动，见 nomineeLists；
 *  - 收益为定额（AwardSystem 常量），获奖者是非玩家时只记名、不动 NPC 数值。
 */
export class AwardSystem {
    private static instance: AwardSystem;

    private constructor() { }

    static getInstance(): AwardSystem {
        if (!this.instance) {
            this.instance = new AwardSystem();
        }
        return this.instance;
    }

    /**
     * 参评档位派生（纯函数，不读存档）：
     *  - 主角档 = roleType 含「主角」——**不看番位**，二番男主角/女主角同样报主角奖
     *    （2026-09-28 用户改定，废掉旧「真二番下沉配角档」；群像剧并列主角本来就是主角档，不受影响）
     *  - 配角档 = 非主角型且 2/3 番
     *  - 其余（4/5 番非主角、龙套）不参评
     */
    deriveTier(script: ScriptDef, roleIndex: number): AwardTier {
        const role = script.roles[roleIndex];
        if (!role) return 'none';
        return this.tierOfRole(role.roleType, role.billing);
    }

    /** 档位派生本体：固定阵容（无 roleIndex）与 roles 位共用同一把尺 */
    private tierOfRole(roleType: string, billing?: number): AwardTier {
        if (roleType.includes('主角')) return 'lead';
        const b = billing ?? 1;
        return b === 2 || b === 3 ? 'supporting' : 'none';
    }

    /** 类别 = 档位 × 角色性别（取 roleType 前缀，与角色定位同源，不受 NPC 性别影响） */
    private categoryOf(tier: AwardTier, roleType: string): AwardCategory | null {
        const male = roleType.startsWith('男');
        if (tier === 'lead') return male ? 'bestActor' : 'bestActress';
        if (tier === 'supporting') return male ? 'bestSupportingActor' : 'bestSupportingActress';
        return null;
    }

    /**
     * 一条可参评的作品记录（玩家与 NPC 归一同构）。
     *
     * ⚠️ `abs` = **杀青月**（2026-09-30 用户改定：作品杀青进播放期即合格，⛔ 等播完）。
     * 三个来源共用同一个时间戳口径 ⇒ 同一 (scriptId, roleIndex) 无论此刻处在哪个阶段，算出来的
     * `abs` 都⛔ 变 ⇒ 天然只落进一届（窗口互不重叠），⛔ 需要"历届已收录"黑名单。
     *  ① 履历行（已播完）：`year/month` 是播完月 ⇒ 回算 杀青月 = 播完月 − 播出期。
     *  ② 玩家在途 `airing` 合同：直接取剧级门控月 `signedMonth + scriptShootMonths − 1`
     *    （与 `advanceAiringContracts` 判定"本月开始滴灌"用的是同一个值）；配角戏份先拍完、
     *    全剧还⛔ 杀青的那些⛔ 进池（还没进播放期）。
     *  ③ NPC 在途 `airing` 工作：`phase` 在杀青当月月末翻成 airing 并滴首月
     *    ⇒ 杀青月 = 当月 − 已滴灌月数。
     *  ④ 固定阵容（`ScriptDef.cast`，2026-10-21）：只在①②③已给出该剧真实制作锚点时追加，
     *    杀青月沿用锚点月；没有制作记录（剧本没拍）则不进池，不许凭空造提名。
     */
    private allWorks(): {
        performer: string; name: string; scriptId: string; roleIndex: number;
        roleName?: string;
        workName: string; roleType: string; genre: ScriptGenre;
        score: number; heat: number; rank: number; billing?: number; abs: number;
    }[] {
        const state = GameState.getInstance();
        const nowAbs = absMonthOf(state.player.year, state.player.month);
        const out: ReturnType<AwardSystem['allWorks']> = [];

        for (const f of state.player.filmography ?? []) {
            const script = GameData.SCRIPT_MAP[f.scriptId];
            // 展示字段读履历自带的快照；剧本整部缺失时仍要跳过——配角档要看该位的 billing，快照给不出
            if (!script) continue;
            out.push({
                performer: 'player',
                name: state.player.name,
                scriptId: f.scriptId,
                roleIndex: f.roleIndex,
                workName: f.workName,
                roleType: f.roleType,
                genre: f.genre,
                billing: script.roles[f.roleIndex]?.billing,
                score: f.score,
                // 玩家侧热度含剧宣累计（撞顶由 workHeatOf 封顶）
                heat: fanSystem.workHeatOf(script),
                rank: state.player.rank,
                abs: absMonthOf(f.year, f.month) - scriptAirMonths(script),
            });
        }

        for (const npc of normalNpcSystem.getNpcs()) {
            for (const f of npc.filmography ?? []) {
                const script = GameData.SCRIPT_MAP[f.scriptId];
                if (!script) continue;
                out.push({
                    performer: npc.id,
                    name: npc.name,
                    scriptId: f.scriptId,
                    roleIndex: f.roleIndex,
                    workName: f.workName,
                    roleType: f.roleType,
                    genre: f.genre,
                    billing: script.roles[f.roleIndex]?.billing,
                    score: f.score,
                    heat: script.heat,
                    rank: npc.rank,
                    abs: absMonthOf(f.year, f.month) - scriptAirMonths(script),
                });
            }
        }

        // ② 玩家已杀青、正在播出
        for (const c of state.player.contracts) {
            if (c.status !== 'airing') continue;
            const script = GameData.SCRIPT_MAP[c.scriptId];
            const role = script?.roles[c.roleIndex];
            if (!script || !role) continue;
            // 开播月（`dramaPremiereAbsOf` 单一出口；2026-10-29 定调＝剧杀青次月、固定日历——
            // 玩家戏份何时拍完都不动它，拖期只走违约）。作品按开播月落届，
            // ⛔ 落进一个早就评完的届。旧档缺 `signedMonth` 时沿用旧口径（排除）。
            const wrapAbs = dramaPremiereAbsOf(c, script, nowAbs)
                ?? (c.signedMonth ?? nowAbs) + scriptShootMonths(script);
            if (nowAbs < wrapAbs) continue;
            out.push({
                performer: 'player', name: state.player.name,
                scriptId: c.scriptId, roleIndex: c.roleIndex,
                workName: script.name, roleType: role.roleType, genre: script.genre,
                billing: role.billing,
                score: c.scoreSnapshot ?? script.baseScore,
                heat: fanSystem.workHeatOf(script), rank: state.player.rank, abs: wrapAbs,
            });
        }

        // ③ NPC 已开播、正在播出。开播月门（2026-10-29 补，与热度榜/剧情钩子同尺）：新钟 job 名义杀青月
        // 就翻 airing，未到开播月 = 剧还没上线 ⛔ 入账；abs 锚日历开播月（开机+违约线窗长），不再按滴灌
        // 进度反推——空窗月会把开播月记成当月、作品落错届。旧档 job 无 shootStartAbs 走旧钟（杀青即
        // 开播）：不设门，abs 仍按已滴灌月数回算。
        for (const npc of normalNpcSystem.getNpcs()) {
            for (const j of npc.activeJobs ?? []) {
                if (j.phase !== 'airing') continue;
                const script = GameData.SCRIPT_MAP[j.scriptId];
                const role = script?.roles[j.roleIndex];
                if (!script || !role) continue;
                const airMonths = Math.max(1, j.airMonths);
                const abs = j.shootStartAbs != null
                    ? scriptPremiereAbsOf(j.shootStartAbs, script)
                    : nowAbs - Math.max(0, airMonths - j.airMonthsLeft);
                if (j.shootStartAbs != null && nowAbs < abs) continue;
                out.push({
                    performer: npc.id, name: npc.name,
                    scriptId: j.scriptId, roleIndex: j.roleIndex,
                    workName: script.name, roleType: role.roleType, genre: script.genre,
                    billing: role.billing,
                    score: script.baseScore, heat: script.heat, rank: npc.rank,
                    abs,
                });
            }
        }

        // 同 (scriptId, roleIndex) 只留一次。**玩家行永远优先**：
        // 季初 sweep 会先把整季所有角色位派给 NPC（玩家还没开始试镜），玩家后来抢到同一个位时
        // 两边会各留一条记录；旧口径比的是播完月，NPC 早接角 ⇒ 播完也早一个月，
        // 「留最早」会把**玩家那条静默丢掉** ⇒ 永无提名（2026-09-29 实测踩到，签约时已改由
        // NormalNpcSystem.revokeRole 回收在途那份）。改成杀青月后两边时间戳通常相同，
        // 玩家优先这条仍是老档影子行的兜底。
        const first = new Map<string, typeof out[number]>();
        for (const w of out) {
            const key = `${w.scriptId}#${w.roleIndex}`;
            const held = first.get(key);
            if (!held) { first.set(key, w); continue; }
            if (held.performer === 'player') continue;          // 玩家在场 ⇒ 该位归玩家
            if (w.performer === 'player' || w.abs < held.abs) first.set(key, w);
        }
        const works = [...first.values()];

        // ④ 固定阵容（ScriptDef.cast）：只在①②③已经给出该剧制作锚点时补。
        // 锚点月 = 该剧全部真实作品里最早的杀青月（同剧各来源同尺，取最小是对老档影子行的兜底）。
        // 无制作记录即剧本没拍，整条不进池（不许凭空造提名，也不许让 902 在没开拍时就占影后名单）。
        const anchorAbs = new Map<string, number>();
        const occupied = new Set<string>();
        for (const w of works) {
            occupied.add(`${w.scriptId}#${w.performer}`);
            const held = anchorAbs.get(w.scriptId);
            if (held === undefined || w.abs < held) anchorAbs.set(w.scriptId, w.abs);
        }
        const npcs = normalNpcSystem.getNpcs();
        for (const [scriptId, anchor] of anchorAbs) {
            const script = GameData.SCRIPT_MAP[scriptId];
            if (!script) continue;
            for (const c of fixedCastCredits(script)) {
                // roleType 缺省 = 固定阵容没写定位，奖项不收录（热度榜仍算主演）；信用口已 warn
                if (!c.roleType) continue;
                const performer = c.npcId ?? `cast:${c.actorName}`;
                if (occupied.has(`${scriptId}#${performer}`)) continue;   // 该演员在本剧已有真实作品行
                const npcRank = c.npcId ? npcs.find(n => n.id === c.npcId)?.rank : undefined;
                works.push({
                    performer,
                    name: c.actorName,
                    scriptId,
                    roleIndex: -1,
                    roleName: c.roleName,
                    workName: script.name,
                    roleType: c.roleType,
                    genre: script.genre,
                    billing: c.billing,
                    score: script.baseScore,
                    heat: script.heat,
                    // 活实例优先（2026-10-23 立，2026-10-24 仍有效）：竞争型特殊 NPC 的咖位是**浮动**的
                    // （现由「阶段接戏门槛 → 接到的戏 → 滴灌名气」自然推出，与普通 NPC 同一条时钟；
                    // 甲案的锚点曲线已作废，但这条优先级不变），表里的 `rival.rank` 只是登场起点。
                    // ⛔ 旧的表值优先 ⇒ 她爬到 Lv.7、评选上仍显示 Lv.5（两把尺，浮动在可见面上白做）。
                    // 资源型特殊 NPC（庄文茵 Lv.9）没有 NormalNpc 实例，仍走表值兜底。
                    rank: npcRank ?? c.rank ?? 0,
                    abs: anchor,
                });
            }
        }
        return works;
    }

    /**
     * 评选总分 = 表演分 + 热度项 + 评审随机（随机在提名时定格，颁奖夜⛔ 重掷）。
     * 2026-09-28 用户改定：⛔ 咖位项（原 `1.0 × rank/10`）、热度项⛔ 系数（原 ×1.5）
     * ⇒ 热度项 = min(1, workHeat/100) ∈ [0,1]；咖位对评选⛔ 直接作用，只经「属性 → 杀青实际评分」间接进到 score 项里。
     */
    private totalOf(w: { score: number; heat: number }): number {
        const heatPart = Math.min(1, w.heat / GameConfig.AWARD_HEAT_DIV);
        const jitter = Utils.randomFloat(-GameConfig.AWARD_JITTER, GameConfig.AWARD_JITTER);
        return Math.round((w.score + heatPart + jitter) * 100) / 100;
    }

    /** 某届四类别的候选名单（未排序、未截名额） */
    candidatesOf(def: CeremonyDef): Partial<Record<AwardCategory, AwardNominee[]>> {
        const award = AWARD_MAP[def.awardId];
        const { startAbs, endAbs } = windowOf(def);
        const buckets: Partial<Record<AwardCategory, AwardNominee[]>> = {};
        for (const w of this.allWorks()) {
            if (w.abs < startAbs || w.abs > endAbs) continue;
            // 分池按履历快照，⛔ 按当前表的 genre（表回改分类会改写历史届次的参赛池）
            if (!award.genres.includes(w.genre)) continue;
            const script = GameData.SCRIPT_MAP[w.scriptId];
            if (!script) continue;
            const tier = w.roleIndex >= 0
                ? this.deriveTier(script, w.roleIndex)
                : this.tierOfRole(w.roleType, w.billing);
            const category = this.categoryOf(tier, w.roleType);
            if (!category) continue;
            (buckets[category] ??= []).push({
                performer: w.performer, name: w.name,
                gender: w.roleType.startsWith('男') ? 'male' : 'female',
                scriptId: w.scriptId, roleIndex: w.roleIndex,
                roleName: w.roleName,
                workName: w.workName, roleType: w.roleType,
                score: w.score, heat: w.heat, rank: w.rank,
                total: this.totalOf(w),
            });
        }
        return buckets;
    }

    /**
     * 提名名额 = min(候选数, min(CAP, max(FLOOR, ⌈候选数 × RATIO⌉)))，即浮动在 4~5 人。
     * 2026-09-28 用户改定：RATIO 0.5→0.67、名额由固定 5 改为浮动（薄池届⛔ 齐刷同一人数）。
     * 2026-09-30 用户改定：上限 6→5（**4~5**）。
     * 最外层 `min(候选数, …)` 是地板：候选不足下限时按实有人数给，⛔ 凭空造提名（宁少提名/空缺）。
     */
    private nomineeCap(candidateCount: number): number {
        return Math.min(
            candidateCount,
            Math.min(
                GameConfig.AWARD_NOMINEE_CAP,
                Math.max(GameConfig.AWARD_NOMINEE_FLOOR,
                    Math.ceil(candidateCount * GameConfig.AWARD_NOMINEE_RATIO)),
            ),
        );
    }

    /**
     * 提名名单生成（公布/补算两处调用点共用，⛔ 规则漂移）：
     * 分桶候选 → 参评线过滤 → 同人限流 → 按 total 截名额。
     * 2026-09-30 茄子拍板（A 案 + 参评线）：
     *  - 参评线：score ≥ AWARD_NOMINEE_MIN_SCORE 才入池（评分通胀修前全挤 10 分无意义，改版后才有区分度；
     *    NPC score=baseScore 剧本质量线、玩家=实际评分可凭演技把低分本拉过线，烂本冲奖关死）；
     *  - 同人限流：每类别每个演员（performer）只入围其 total 最高的**达标**作品——提名单位=演员而非作品位，
     *    旧口径头部 NPC 多部同入围一人包场（实测）。**先过滤再选代表作**：有过线作品的演员，
     *    代表作 = 过线作品里 total 最高者（若先选代表作再过滤，会出现「最高分的那部不过线 → 整人出局」的误伤）；
     *  - 薄池届宁可少于下限/空缺（vacant），⛔ 为凑数放水。
     */
    private nomineeLists(buckets: Partial<Record<AwardCategory, AwardNominee[]>>): Partial<Record<AwardCategory, AwardNominee[]>> {
        const lists: Partial<Record<AwardCategory, AwardNominee[]>> = {};
        for (const category of AWARD_CATEGORY_ORDER) {
            const pool = (buckets[category] ?? [])
                .filter(n => n.score >= GameConfig.AWARD_NOMINEE_MIN_SCORE)
                .slice()
                .sort((a, b) => b.total - a.total);
            const perCap = new Map<string, number>();
            const shortlist: AwardNominee[] = [];
            for (const n of pool) {
                const used = perCap.get(n.performer) ?? 0;
                if (used >= GameConfig.AWARD_PERFORMER_NOMINEE_CAP) continue;   // 同人已取其更高分的代表作
                perCap.set(n.performer, used + 1);
                shortlist.push(n);
            }
            lists[category] = shortlist.slice(0, this.nomineeCap(shortlist.length));
        }
        return lists;
    }

    /** 本届是否已结算过（幂等保护：结局年同月可能被重复进入决策日） */
    private alreadySettled(def: CeremonyDef): boolean {
        const history = GameState.getInstance().player.awardHistory ?? [];
        return history.some(r => r.awardId === def.awardId && r.edition === def.edition);
    }

    /** 该届的颁奖绝对月份 */
    private ceremonyAbs(def: CeremonyDef): number {
        return absMonthOf(def.yearIndex, def.month);
    }

    /**
     * 评奖窗口知情（2026-09-29 剧组等主角批 B，排程知情提示用）：
     * 按当前节奏（剩余戏份最快拍完 + 剧级档期 + 播出期）算最快播完月，对照与本剧类型匹配的
     * 最近一届窗口右端——'missed' = 本届已赶不上（拖延真实代价：下一届在 3 年后）、
     * 'tight' = 压线（窗口右端前 1 个月内播完）、'ok' = 从容。
     * null = 该类型不参评（short/variety）或已无可参评届次。⛔ 只做提示，不参与资格判定。
     */
    awardDeadlineInfo(script: ScriptDef, contract: Contract): { status: 'ok' | 'tight' | 'missed'; awardName: string } | null {
        const state = GameState.getInstance();
        const nowAbs = absMonthOf(state.player.year, state.player.month);
        const upcoming = CEREMONIES
            .filter(c => !c.scripted && AWARD_MAP[c.awardId].genres.includes(script.genre) && this.ceremonyAbs(c) > nowAbs)
            .sort((a, b) => this.ceremonyAbs(a) - this.ceremonyAbs(b))[0];
        if (!upcoming) return null;
        const { endAbs } = windowOf(upcoming);
        // 2026-09-30 参评判据改成「杀青进播放期」⇒ 这里比的是**最快杀青月**（播出期⛔ 再算进死线）；
        // 2026-10-03 复核修正：杀青月口径对齐全站统一尺 isDramaAired（去旧 −1）。
        // 2026-10-27 日历化批：杀青月一律读 dramaTimelineOf（原先此处在本地手抄一份公式，
        // 且漏了 signedMonth 空判；现在与 allWorks / 热度榜 / 滴灌门控读同一份日历）。
        const calendar = dramaTimelineOf(contract, script, nowAbs);
        const leftWeeks = Math.max(0, scriptSystem.shotWeeksLeftOf(contract));
        const fastSelfAbs = nowAbs + Math.ceil(leftWeeks / GameConfig.WEEKS_PER_MONTH);
        const wrapAbs = Math.max(calendar.wrapUp, fastSelfAbs);
        const awardName = AWARD_MAP[upcoming.awardId].name;
        if (wrapAbs > endAbs) return { status: 'missed', awardName };
        if (endAbs - wrapAbs <= 1) return { status: 'tight', awardName };
        return { status: 'ok', awardName };
    }

    /**
     * 月初公布提名名单。
     * 2026-09-30 用户改定：从「月末结算尾部」挪到**定格月月初、玩家点「确认行程并进入下月」的那一刻**
     * （挂点见 `GameLoop.confirmMonth`）——定格月 = 颁奖月的前一个自然月（1 月颁奖 ⇒ 12 月月初公布），
     * 窗口右端 = 定格月的上一自然月末（= 11 月末，见 Awards.endAbsOf）。
     * 三件事对齐才有意义：① 播报与颁奖之间隔着**整整一个可玩的月**（旧写法 12 月末公布 → 1 月月初颁奖，
     * 玩家一步都没走过，「颁奖就在下个月」必然是马后炮，还会被颁奖夜抢屏挤到得主之后）；
     * ② 参评判据已是杀青月 ⇒ 12 月内才杀青的本自动归下一届，⛔ 两届之间的缝隙丢本；
     * ③ 玩家没点确认（直接退出/该月结局）⇒ 由 `settleDueBeforeGameOver()` 兜底进 `awardHistory`，⛔ 有届无名
     *    （批 G 二改：`onMonthStart` 只管生涯最后一届，⛔ 再替评选届补算）。
     */
    publishNominations(): void {
        const state = GameState.getInstance();
        const nowAbs = absMonthOf(state.player.year, state.player.month);
        const def = CEREMONIES.find(c => !c.scripted && this.ceremonyAbs(c) - 1 === nowAbs);
        if (!def) return;
        // 收官届⛔ 定格⛔ 播报（丙′ v2.3「结算时现算」）：颁奖就在退役那个决策日当场完成，
        // 「下月颁奖」的定格/播报语义不适用；名单在结算时刻现算（含 12 月末杀青告别作）
        if (this.isCareerFinal(def)) return;
        if (this.alreadySettled(def) || state.player.pendingAward) return;

        const buckets = this.candidatesOf(def);
        const lists = this.nomineeLists(buckets);
        state.player.pendingAward = { awardId: def.awardId, edition: def.edition, lists };
        this.notifyNomination(def, lists);
    }

    /**
     * 小助理播报：玩家自己入围时才打扰（未入围只是业内新闻，名单⛔ 报给玩家，下月颁奖夜面板照常弹）。
     * 口径（2026-09-30 用户定）＝只说「入围了哪个奖 + 颁奖在下个月」，⛔ 念提名名单。
     */
    private notifyNomination(def: CeremonyDef, lists: Partial<Record<AwardCategory, AwardNominee[]>>): void {
        const award = AWARD_MAP[def.awardId];
        const cats = AWARD_CATEGORY_ORDER
            .map(cat => ({ cat, mine: (lists[cat] ?? []).find(n => n.performer === 'player') }))
            .filter(x => !!x.mine);
        if (cats.length === 0) return;
        // 2026-10-25 台词表导码（docs/夏夏台词-文案填写表.md #10）：多项=首项+「等N项」
        assistantSystem.push(
            `~第${def.edition}届${award.name}入围名单公布了，你的《${cats[0].mine!.workName}》入围了「${award.categoryNames[cats[0].cat]}」${cats.length > 1 ? `等${cats.length}项` : ''}。颁奖在下个月。`,
            { kind: 'award' },
        );
    }

    /**
     * 决策日开始时调用（`GameLoop.enterDecisionDay` 放在结局判定之前）。
     * 批 G（2026-09-30 定稿）起这里**只**管一类"当场播"的届 = **生涯最后一届**（金枢 29）：
     * 它恰好落在 40 岁退役那个月，玩家一进决策日就被判定退役、永远走不到「确认行程」那一步，
     * 所以颁奖夜⛔ 挪到行程开始前，否则那届会凭空消失。
     * 其余届（含开场剧情届）一律由 `onMonthConfirm()` 在玩家点「确认行程并进入下月」时结算并播出。
     */
    onMonthStart(): void {
        this.settleDue(def => this.isCareerFinal(def));
    }

    /**
     * 玩家点「确认行程并进入下月」时调用（`GameLoop.confirmMonth`）：结算本月到期的届。
     * 开场剧情届（第 1 年 1 月，玩家还没入行、纯观众）也走这条路 ⇒ 开局要先排满 1 月四周、
     * 点确认才看到「苏曼卿封后」那一夜（2026-09-30 用户改口：与其余届同一口径，⛔ 特例）。
     */
    onMonthConfirm(): void {
        this.settleDue(def => !this.isCareerFinal(def));
    }

    /**
     * 结局判定前补结算（`EndingSystem.endGame` 头部）：阶段失败 / 主动退圈都可能正好落在颁奖月，
     * 而那条路径上玩家走不到确认 ⇒ 这一届⛔ 演出（马上要切结算页），但结果与收益必须落进 `awardHistory`，
     * 让结算页荣誉墙与 `CareerSummary` 快照⛔ 少一届。
     */
    settleDueBeforeGameOver(): void {
        this.settleDue(() => true);
    }

    /** 生涯最后一届＝全部届次里颁奖时间最晚的那一届（它与 40 岁退役月同月，是批 G 例外的判据） */
    private isCareerFinal(def: CeremonyDef): boolean {
        return this.ceremonyAbs(def) === Math.max(...CEREMONIES.map(c => this.ceremonyAbs(c)));
    }

    /** 本月到期、且被 `mine` 挑中的届逐条结算（已结算过的跳过 ⇒ 重复进入决策日幂等） */
    private settleDue(mine: (def: CeremonyDef) => boolean): void {
        const state = GameState.getInstance();
        const nowAbs = absMonthOf(state.player.year, state.player.month);
        for (const def of CEREMONIES) {
            // ⛔ 「过期就补」的宽容匹配：那样开场届会在第 3 年才结算、记录年份跟着错格——
            //    开局那一届由 gameManager.newGame() 直接触发钩子落地（第 1 年 1 月），旧档按「不回填」定稿不管
            if (this.ceremonyAbs(def) !== nowAbs || !mine(def)) continue;
            if (this.alreadySettled(def)) continue;
            if (def.scripted) {
                this.settleScripted(def);
            } else if (!this.isCareerFinal(def) && state.player.pendingAward
                && state.player.pendingAward.awardId === def.awardId
                && state.player.pendingAward.edition === def.edition) {
                this.settle(def, state.player.pendingAward.lists);
                state.player.pendingAward = null;
            } else {
                // 提名环节没跑到（玩家在定格月没点确认就退出）：当场补算一次，绝不出现「有届无名」。
                // 收官届恒走本分支（丙′ v2.3 §五.0 拍板「结算时现算」）：⛔ 消费 12 月初定格的名单
                // （告别作 12 月末才杀青、进不了定格名单），按结算时刻全量履历现算——
                // 窗口右端已对齐生涯末日（Awards.CAREER_LAST_ABS），12 月杀青告别作可入围可获奖。
                this.settle(def, this.nomineeLists(this.candidatesOf(def)));
            }
        }
    }

    /** 开场剧情届：得主写死，不吃作品记录、不发收益 */
    private settleScripted(def: CeremonyDef): void {
        const state = GameState.getInstance();
        const history = (state.player.awardHistory ??= []);
        for (const category of AWARD_CATEGORY_ORDER) {
            const win = def.scripted?.[category];
            history.push({
                awardId: def.awardId, edition: def.edition, category,
                year: state.player.year, vacant: !win,
                nominees: win ? [{
                    performer: 'npc_scripted', name: win.name,
                    gender: category === 'bestActor' || category === 'bestSupportingActor' ? 'male' : 'female',
                    scriptId: '', roleIndex: -1, workName: win.workName,
                    roleType: '', score: 0, heat: 0, rank: 0, total: 0,
                }] : [],
                playerWon: false, playerNominated: false,
                scripted: win ? { name: win.name, workName: win.workName } : undefined,
            });
        }
    }

    /** 颁奖结算：每个类别取名单首位为得主，玩家按获奖/提名发定额收益 */
    private settle(def: CeremonyDef, lists: Partial<Record<AwardCategory, AwardNominee[]>>): void {
        const state = GameState.getInstance();
        const award = AWARD_MAP[def.awardId];
        const history = (state.player.awardHistory ??= []);

        for (const category of AWARD_CATEGORY_ORDER) {
            const nominees = (lists[category] ?? [])
                .slice()
                .sort((a, b) => b.total - a.total);
            const winner = nominees[0];
            const record: AwardRecord = {
                awardId: def.awardId, edition: def.edition, category,
                year: state.player.year,
                vacant: !winner,
                nominees,
                playerWon: winner?.performer === 'player',
                playerNominated: nominees.some(n => n.performer === 'player'),
            };
            history.push(record);

            if (record.playerWon) {
                this.payOut(award, category, true);
            } else if (record.playerNominated) {
                this.payOut(award, category, false);
                const mine = nominees.find(n => n.performer === 'player')!;
            } else if (winner) {
            } else {
            }
        }
    }

    /**
     * 玩家收益发放。**同一奖同一类别重复获奖不再衰减**（2026-10-21 用户令：删掉复获递减，
     * 名气与口碑都按定额给满；原先是 `AWARD_REPEAT_FACTOR = 0.5` 递减）。
     * 口碑为定额（属性制不受比例化约束）；片酬不受影响；Tag 由 addTag 幂等天然不叠加。
     */
    private payOut(award: AwardDef, category: AwardCategory, isWin: boolean): void {
        const state = GameState.getInstance();
        // 获奖那笔名气乘「奖杯滚雪球」类正面特性的倍率（⛔ 提名不乘：提名频次高，×N 会变成刷名气的口子；
        // ⛔ 口碑/作品滴灌/营销营业都不受影响）。
        const fame = isWin
            ? state.awardFameGain(GameConfig.AWARD_FAME_WIN)
            : GameConfig.AWARD_FAME_NOM;
        const rep = isWin ? GameConfig.AWARD_REP_WIN : GameConfig.AWARD_REP_NOM;
        state.addFame(fame);
        state.addAttribute('reputation', rep);
        if (isWin) state.addTag(award.tagIds[category]);
    }

    /** 供 UI/测试：某届的定义（存档里只存 awardId + edition） */
    ceremonyOf(awardId: AwardId, edition: number): CeremonyDef | undefined {
        return CEREMONIES.find(c => c.awardId === awardId && c.edition === edition);
    }

    // ===== 颁奖夜 UI（批 B）：只读，⛔ 不改任何结算结果 =====

    /**
     * 有「已结算但没播过」的届次吗（同届 4 条共用一个 key）。
     * 判据只看落盘的 ceremonySeen ⇒ 关掉页面再进来该届仍会补播，播过就绝不再弹。
     */
    unseenCeremony(): { awardId: AwardId; edition: number } | null {
        const seen = GameState.getInstance().player.ceremonySeen ?? [];
        for (const r of GameState.getInstance().player.awardHistory ?? []) {
            if (!seen.includes(`${r.awardId}-${r.edition}`)) {
                return { awardId: r.awardId as AwardId, edition: r.edition };
            }
        }
        return null;
    }

    /** 某届的四条记录，按揭晓顺序（配角先行、主角压轴） */
    recordsOf(awardId: string, edition: number): AwardRecord[] {
        const history = GameState.getInstance().player.awardHistory ?? [];
        return AWARD_CATEGORY_ORDER
            .map(cat => history.find(r => r.awardId === awardId && r.edition === edition && r.category === cat))
            .filter((r): r is AwardRecord => !!r);
    }

    /** 颁奖夜播完（玩家点了「知道了」）才记已播 */
    markCeremonySeen(awardId: string, edition: number): void {
        const seen = (GameState.getInstance().player.ceremonySeen ??= []);
        const key = `${awardId}-${edition}`;
        if (!seen.includes(key)) seen.push(key);
    }

    /**
     * 玩家本届实际到手的收益。UI ⛔ 自己套系数：这里的算式与 payOut() 同源。
     */
    payoutOfCeremony(awardId: string, edition: number): { fame: number; rep: number; tagIds: string[] } | null {
        const state = GameState.getInstance();
        const history = state.player.awardHistory ?? [];
        let fame = 0, rep = 0;
        const tagIds: string[] = [];
        for (let i = 0; i < history.length; i++) {
            const r = history[i];
            if (r.awardId !== awardId || r.edition !== edition) continue;
            if (!r.playerWon && !r.playerNominated) continue;
            if (r.playerWon) {
                // ⛔ 与 payOut 同口径：获奖那笔乘「奖杯滚雪球」类倍率，
                //    且**已得奖数只算到本条为止**（含本条）——不能读全量 history，否则把后来的奖也算进去。
                const winsUpTo = history.slice(0, i + 1).filter(h => h.playerWon).length;
                fame += state.awardFameGain(GameConfig.AWARD_FAME_WIN, winsUpTo);
            } else {
                fame += GameConfig.AWARD_FAME_NOM;      // 提名不乘
            }
            rep += r.playerWon ? GameConfig.AWARD_REP_WIN : GameConfig.AWARD_REP_NOM;
            if (r.playerWon) tagIds.push(AWARD_MAP[r.awardId as AwardId].tagIds[r.category]);
        }
        return fame > 0 || rep > 0 ? { fame, rep, tagIds } : null;
    }

    /** 当前是否有未兑现的提名（颁奖页/调试用） */
    peekPending(): { awardId: AwardId; edition: number } | null {
        const p = GameState.getInstance().player.pendingAward;
        return p ? { awardId: p.awardId as AwardId, edition: p.edition } : null;
    }

    /** 调试辅助：把当前档的咖位名读出来（NPC 走男体名由调用方决定） */
    rankName(rank: number, gender?: 'male' | 'female'): string {
        return rankSystem.getRankName(rank, gender);
    }
}

/** 导出单例 */
export const awardSystem = AwardSystem.getInstance();
